import AuiButton from "../../components/Buttons/Buttons";
import CheckBox from "../../components/CheckBox/CheckBox";
import DropDown from "../../components/DropDown/DropDown";
import Headings from "../../components/Headings/Headings";
import Modals from "../../components/Modals/Modals";
import "./UserDetails.scss";
import uploadIcon from "../../assets/images/upload-icon.svg";
import uploadDisableIcon from "../../assets/images/upload-disable-icon.svg";
import { useEffect, useState } from "react";
import {
  fetchCadreList,
  fetchWorkplaceList,
} from "../../services/organization.service";
import {
  createConsentRequest,
  uploadConsentDocument,
} from "../../services/training-session.service";
import { useDispatch } from "react-redux";
import { showAlert } from "../../store/alertSlice";
import { useNavigate } from "react-router-dom";
import { CryptoMethods } from "../../utils/crypto-methods";
import SearchableSingleSelectDropdown from "../../components/DynamicDropdowns/SearchableSingleSelectDropdown";
import { useDebouncedSearch } from "../../utils/constants/app-constants";
import Input from "../../components/Input/Input";

export interface DropdownOption {
  id?: number;
  name?: string;
  code?: string;
}

export interface ConsentForm {
  firstName?: string;
  lastName?: string;
  email?: string;
  workplace?: DropdownOption;
  cadre?: DropdownOption;
  role?: DropdownOption;
  hasGivenConsent?: boolean;
}

const UserConsent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [workplaceList, setWorkplaceList] = useState<DropdownOption[]>([]);
  const [cadreList, setCadreList] = useState<DropdownOption[]>([]);
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [consentFormData, setConsentFormData] = useState<ConsentForm>({});
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [isFormValid, setIsFormValid] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<FileList | null | string>(
    null
  );
  const [isConsentSubmitLoading, setIsConsentSubmitLoading] = useState(false);
  const [supportingDocPath, setSupportingDocPath] = useState<string>("");
  const [isClearDisabled, setIsClearDisabled] = useState<boolean>(true);
  const [clearError, setClearError] = useState(false);
  const [isCadreLoading, setIsCadreLoading] = useState(true);
  const [isWorkplaceLoading, setIsWorkplaceLoading] = useState(true);
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const roleList = [
    { id: 1, name: "BEC Provider" },
    { id: 2, name: "Master Trainer" },
    { id: 3, name: "Provisional Facilitator" },
    { id: 4, name: "Registered Facilitator" },
  ];

  const fetchCadreDropdown = async (search = "") => {
    try {
      setIsCadreLoading(true);
      const fetchedCadres = await fetchCadreList(search);
      let merged: DropdownOption[] = [...fetchedCadres];
      if (
        consentFormData?.cadre &&
        !merged.some((c) => c.code === consentFormData.cadre?.code)
      ) {
        merged = [consentFormData.cadre, ...merged];
      }
      const mergedCadreList = merged.reduce((acc: DropdownOption[], curr) => {
        if (!acc.find((item) => item.code === curr.code)) {
          acc.push(curr);
        }
        return acc;
      }, []);
      setCadreList(mergedCadreList);
    } catch (error) {
      console.error("Error fetching cadres:", error);
    } finally {
      setIsCadreLoading(false);
    }
  };

  const fetchWorkplaceDropdown = async (search = "") => {
    try {
      setIsWorkplaceLoading(true);
      const fetchedWorkplaces = await fetchWorkplaceList(search);
      let merged: DropdownOption[] = [...fetchedWorkplaces];
      if (
        consentFormData?.workplace &&
        !merged.some((c) => c.code === consentFormData.workplace?.code)
      ) {
        merged = [consentFormData.workplace, ...merged];
      }
      const mergedWorkplaceList = merged.reduce(
        (acc: DropdownOption[], curr) => {
          if (!acc.find((item) => item.code === curr.code)) {
            acc.push(curr);
          }
          return acc;
        },
        []
      );
      setWorkplaceList(mergedWorkplaceList);
    } catch (error) {
      console.error("Error fetching workplaces:", error);
    } finally {
      setIsWorkplaceLoading(false);
    }
  };

  useEffect(() => {
    fetchCadreDropdown();
  }, []);

  useEffect(() => {
    fetchWorkplaceDropdown();
  }, []);

  const debouncedSearchWorkplace = useDebouncedSearch(fetchWorkplaceDropdown)
  const debouncedSearchCadre = useDebouncedSearch(fetchCadreDropdown);

  useEffect(() => {
    const updateFirstName = (event: any) => {
      setFirstName(event.detail);
    };
    const updateLastName = (event: any) => {
      setLastName(event.detail);
    };
    const updateEmail = (event: any) => {
      setEmail(event.detail);
    };
    document
      .querySelector("aui-input#firstName")
      ?.addEventListener("auiInputChange", updateFirstName);
    document
      .querySelector("aui-input#lastName")
      ?.addEventListener("auiInputChange", updateLastName);
    document
      .querySelector("aui-input#email")
      ?.addEventListener("auiInputChange", updateEmail);
    return () => {
      document
        .querySelector("aui-input#firstName")
        ?.removeEventListener("auiInputChange", updateFirstName);
      document
        .querySelector("aui-input#lastName")
        ?.removeEventListener("auiInputChange", updateLastName);
      document
        .querySelector("aui-input#email")
        ?.removeEventListener("auiInputChange", updateEmail);
    };
  }, []);

  const updateForm = (field: string, value: string): void => {
    const listMapper: Record<string, DropdownOption[]> = {
      workplace: workplaceList,
      cadre: cadreList,
      role: roleList,
    };
    if (["workplace", "cadre", "role"].includes(field)) {
      setConsentFormData({
        ...consentFormData,
        [field]: listMapper[field].find((item) => item.name === value),
      });
    }
  };

  useEffect(() => {
    setIsFormValid(
      !!firstName.trim() &&
        !!lastName.trim() &&
        !!email.trim() &&
        !!consentFormData?.workplace?.id &&
        !!consentFormData?.cadre?.id &&
        !!consentFormData?.role?.id &&
        !!consentFormData?.hasGivenConsent &&
        !!supportingDocPath
    );
  }, [firstName, lastName, email, consentFormData, supportingDocPath]);

  useEffect(() => {
    const isFormFilled = !!(
      firstName.trim() ??
      lastName.trim() ??
      email.trim() ??
      consentFormData?.workplace?.id ??
      consentFormData?.cadre?.id ??
      consentFormData?.role?.id ??
      consentFormData?.hasGivenConsent ??
      supportingDocPath
    );
    setIsClearDisabled(!isFormFilled);
  }, [firstName, lastName, email, consentFormData, supportingDocPath]);  

  const handleResetStudentDocModal = () => {
    setRefreshKey((prevKey) => prevKey + 1);
    setUploadedFiles(null);
  };

  const handleFileChange = (files: FileList | null) => {
    if (files) {
      setUploadedFiles(files);
    } else {
      setUploadedFiles(null);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConsentFormData({
      ...consentFormData,
      hasGivenConsent: e.target.checked,
    });
  };
  
  const handleSubmitUserDetail = async () => {
    if (!isFormValid) {
      return;
    }
    setIsConsentSubmitLoading(true);
    const payload: Record<string, any> = {
      firstName: await CryptoMethods.ecbEncryptData(firstName),
      lastName: await CryptoMethods.ecbEncryptData(lastName),
      email: await CryptoMethods.ecbEncryptData(email.toLowerCase()),
      hospitalCode: consentFormData.workplace?.code,
      cadreCode: consentFormData.cadre?.code,
      role: consentFormData.role?.name
        ? consentFormData.role.name.toUpperCase()
        : null,
      supportingDocPath: supportingDocPath,
      isConsentGiven: true,
    };
    const userConsentDetail = await createConsentRequest(payload);
    setIsConsentSubmitLoading(false);
    if (!userConsentDetail?.error) {
      dispatch(
        showAlert({
          message: "User consent request has been sent successfully!",
          type: "success",
        })
      );
      navigate("/");
    }
  };

  const handleUpload = async () => {
    if (uploadedFiles && uploadedFiles.length > 0) {
      const fileToUpload: any = uploadedFiles[0];
      const role = consentFormData.role?.name
        ? consentFormData.role.name.toUpperCase()
        : "";
      const response = await uploadConsentDocument(
        fileToUpload,
        "USER SUPPORTING DOCUMENTS",
        role
      );
      if (
        response.status === 200 &&
        response.data?.user?.status === "COMPLETED"
      ) {
        const filePath = response.data.user.filePath;
        setSupportingDocPath(filePath);
        dispatch(
          showAlert({
            message: "User consent document uploaded successfully!",
            type: "success",
          })
        );
      }
    }
  };

  const clearFormInput = () => {
    if (isClearDisabled) {
      return;
    }
    setFirstName("");
    setLastName("");
    setEmail("");
    setConsentFormData({
      hasGivenConsent: false,
    });
    setSupportingDocPath("");
    setClearError(true);
    setTimeout(() => setClearError(false), 0);
  };

  return (
    <main className="userConsent container">
      <div className="border-bottom my-4">
        <Headings size="h1">User Consent Form</Headings>
      </div>
      <div className="pb-5 userFormSection">
        <div className="row mb-3">
          <div className="col-12 col-md-6 col-lg-4 mt-6">
            <Input
              id="firstName"
              label="First Name"
              value={firstName}
              placeholder="Enter First Name"
              required={true}
              errorMessage="First Name is Mandatory"
              showError={firstNameError && !firstName.trim()}
              onChange={(val) => {
                setFirstName(val);
                setFirstNameError(true);
              }}
            />
          </div>
          <div className="col-12 col-md-6 col-lg-4 mt-6">
            <Input
              id="lastName"
              label="Last Name"
              value={lastName}
              placeholder="Enter Last Name"
              required={true}
              errorMessage="Last Name is Mandatory"
              showError={lastNameError && !lastName.trim()}
              onChange={(val) => {
                setLastName(val);
                setLastNameError(true);
              }}
            />
          </div>
          <div className="col-12 col-md-6 col-lg-4 mt-6">
            <Input
              id="email"
              label="Email ID"
              value={email}
              placeholder="Enter Email ID"
              required={true}
              errorMessage="Email ID is Mandatory"
              showError={emailError && !email.trim()}
              type="text"
              onChange={(val) => {
                setEmail(val);
                setEmailError(true);
              }}
            />
          </div>
          <div className="col-12 col-md-6 col-lg-4 mt-6">
            <SearchableSingleSelectDropdown
              dropdownLabel="Primary Workplace"
              placeHolder="Select Primary Workplace"
              required
              listInBtn
              data={workplaceList?.map((workPlace) => workPlace.name ?? "")}
              mainId="workplace"
              onChange={(value) => updateForm("workplace", value)}
              value={consentFormData?.workplace?.name ?? ""}
              isLoading={isWorkplaceLoading}
              errorText="Primary Workplace is Mandatory"
              clearError={clearError}
              onSearch={debouncedSearchWorkplace}
              dropId= "Primary Workplace"
            />
          </div>
          <div className="col-12 col-md-6 col-lg-4 mt-6">
            <SearchableSingleSelectDropdown
              dropdownLabel="Cadre"
              placeHolder="Select Cadre"
              required
              listInBtn
              data={cadreList?.map((cadre) => cadre.name ?? "")}
              mainId="cadre"
              isLoading={isCadreLoading}
              errorText="Cadre is Mandatory"
              onChange={(value) => updateForm("cadre", value)}
              value={consentFormData.cadre?.name ?? ""}
              clearError={clearError}
              onSearch={debouncedSearchCadre}
              dropId= "cadre"
            />
          </div>
          <div className="col-12 col-md-6 col-lg-4 mt-6">
            <DropDown
              dropdownLabel="Role"
              placeHolder="Select Role"
              required
              listInBtn
              dropId="selectRole"
              data={roleList?.map((role) => role.name)}
              onChange={(value) => updateForm("role", value)}
              value={consentFormData.role?.name ?? ""}
              errorText="Role is Mandatory"
              clearError={clearError}
            ></DropDown>
          </div>
          <div className="col-12 mt-4 studentsDocUpload pt-2">
            <label className="upload-text" htmlFor="uploadInput">
              Supporting Documents<span className="mandatory-asterisk">*</span>
            </label>
            <div className="doc-upload-modal">
              {!supportingDocPath && (
                <div
                  className={
                    !consentFormData.role ? "removePointer" : "addPointer"
                  }
                >
                  <Modals
                    modalButtonTitle="Upload Support Documents"
                    modalType="upload-modal"
                    modalsize="auimodal-md"
                    headingAlign="left"
                    modalTitle="Upload Supported Documents"
                    footerAlign="right"
                    buttonTitle="Upload"
                    modalid="consentSupportModal"
                    variant="secondary"
                    disableActionButton={!consentFormData.role}
                    customIcon={
                      <img
                        className="documnetUploadIcon"
                        src={
                          !consentFormData.role ? uploadDisableIcon : uploadIcon
                        }
                        alt="Upload Icon"
                      />
                    }
                    key={refreshKey}
                    uploadedFiles={uploadedFiles}
                    setUploadedFiles={setUploadedFiles}
                    resetModal={handleResetStudentDocModal}
                    auiFileUploadProps={{
                      allowedfiletypes: ".zip",
                      maxuploadsize: 5000000,
                      iseditable: false,
                      fileuploadlabel: true,
                      auifiluploaddirection: "column",
                      auifiluploaderrorstate: false,
                      auifiluploadrequired: true,
                      auifiluploadrequiredmessage:
                        "Upload files should be mandatory",
                      auifileuploaderrormessage:
                        "Please upload the correct format",
                      onChange: handleFileChange,
                    }}
                    action={handleUpload}
                  ></Modals>
                </div>
              )}
              {supportingDocPath && (
                <span className="">
                  {supportingDocPath?.split("/")?.[1]}
                  <button className="ml-2" onClick={() => setSupportingDocPath("")}>
                    <aui-icon
                      svgclass="aui-icon-cross"
                      pathclass="aui-path-cross"
                      icon="closeIcon"
                      svgwidth="10"
                      svgheight="10"
                      aria-label="Delete file"
                    ></aui-icon>
                  </button>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="d-flex">
        <div>
          <CheckBox
            type="checkbox"
            labelText="I consent that these details can be added to the database"
            required
            onChange={handleCheckboxChange}
            checked={consentFormData.hasGivenConsent || false}
          ></CheckBox>
        </div>
        <div></div>
      </div>
      <div className="border-top d-flex py-4 justify-content-end">
        <AuiButton
          variant="secondary"
          buttontitle="Clear"
          buttonid="clearStudent"
          size="medium"
          onClick={clearFormInput}
          disabled={isClearDisabled}
        ></AuiButton>
        <AuiButton
          variant="primary"
          buttontitle="Submit"
          buttonid="submitStudent"
          buttonclass="ml-3"
          size="medium"
          disabled={!isFormValid}
          onClick={handleSubmitUserDetail}
          loading={isConsentSubmitLoading}
        ></AuiButton>
      </div>
    </main>
  );
};
export default UserConsent;
